import {
  buttonArguments,
  JoystickActionConfig,
  JoystickInput,
  stickAxisMap,
  StickId,
} from '@/providers';

/**
 * L2/R2 are analog; we only treat them as **intentional** when pulled to the end — a small squeeze
 * must not move between camera streams or touch fullscreen transitions. `button.pressed` would
 * fire ~mid-travel on many pads; instead we require `value` near **1** to register, then keep
 * “held” until `value` drops meaningfully so one RAF glitch does not clear `held` mid-squeeze.
 */
const TRIGGER_FULL_PULL_MIN = 0.97;
const TRIGGER_RELEASE_BELOW = 0.85;
const analogTriggerLatched = new Map<number, boolean>();

export const resetAnalogTriggerLatch = () => {
  analogTriggerLatched.clear();
};

const analogTriggerIsPressed = (buttonIndex: number, button: GamepadButton): boolean => {
  const v = button.value ?? 0;
  const latched = analogTriggerLatched.get(buttonIndex) ?? false;
  if (latched) {
    if (v < TRIGGER_RELEASE_BELOW) {
      analogTriggerLatched.set(buttonIndex, false);
      return false;
    }
    return true;
  }
  if (v >= TRIGGER_FULL_PULL_MIN) {
    analogTriggerLatched.set(buttonIndex, true);
    return true;
  }
  return false;
};

/** Turn current gamepad snapshot into a set of active input ids we care about */
export const sampleActiveInputs = (
  gp: Gamepad,
  start: Partial<Record<JoystickInput, JoystickActionConfig>>,
  axisThreshold: number
): Map<string, buttonArguments> => {
  const active = new Map<string, buttonArguments>();

  //#region buttons
  for (let i = 0; i < gp.buttons.length; i++) {
    const button = gp.buttons[i];
    const id = `b${i}`;
    if (!button || !start[id as JoystickInput]) {
      continue;
    }

    const pressed = i === 6 || i === 7 ? analogTriggerIsPressed(i, button) : button.pressed;

    if (!pressed) {
      continue;
    }

    active.set(id, null); // buttons don't need arguments
  }
  //#endregion buttons

  //#region axes / sticks
  (Object.keys(stickAxisMap) as StickId[]).forEach(stickId => {
    // skip if there is no start/stop handler configured for this stick
    if (!start[stickId]) {
      return;
    }

    const [xIndex, yIndex] = stickAxisMap[stickId];
    const x = gp.axes[xIndex] ?? 0;
    const y = gp.axes[yIndex] ?? 0;

    if (Math.abs(x) > axisThreshold || Math.abs(y) > axisThreshold) {
      active.set(stickId, { x, y });
    }
  });
  //#endregion

  return active;
};

/** stop all held inputs */
export const releaseAll = (
  held: Set<string>,
  inputConfigurations: Partial<Record<string, JoystickActionConfig>>,
  consumedUntilPhysicalRelease?: Set<string>
) => {
  if (!held.size && !consumedUntilPhysicalRelease?.size) return;

  held.forEach(id => {
    inputConfigurations[id]?.stopHandler?.();
  });
  held.clear();
  consumedUntilPhysicalRelease?.clear();
};

/**
 * True when the hardware input is fully released (not merely missing from one RAF sample).
 * Used so `repeat: false` fires once per physical press even if `sampleActiveInputs` flickers.
 */
export const isJoystickInputPhysicallyReleased = (
  gp: Gamepad,
  inputId: string,
  axisThreshold: number
): boolean => {
  const buttonMatch = /^b(\d+)$/i.exec(inputId);
  if (buttonMatch) {
    const idx = Number(buttonMatch[1]);
    const button = gp.buttons[idx];
    if (!button) return true;
    if (idx === 6 || idx === 7) {
      return !analogTriggerIsPressed(idx, button);
    }
    return !button.pressed;
  }
  if (stickAxisMap[inputId as StickId]) {
    const [xi, yi] = stickAxisMap[inputId as StickId];
    const x = gp.axes[xi] ?? 0;
    const y = gp.axes[yi] ?? 0;
    return Math.abs(x) <= axisThreshold && Math.abs(y) <= axisThreshold;
  }
  return true;
};

/** normalize mapping keys to lowercase, e.g. "B0" -> "b0", "A1+" -> "a1+" */
export const norm = (k: string) => k.toLowerCase();
